'use client'

import { Locale } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { Dispatch, RefObject, SetStateAction, SyntheticEvent, useEffect, useState } from 'react'
import { useDebounceCallback, useEventListener, useTimeout } from 'usehooks-ts'

import ViverseLogoOverlay from '@/app/[locale]/[roomId]/components/ViverseLogoOverlay'
import { BI_CATEGORY_BEHAVIOR_IN_ROOM, BI_EVENT_VIEW_COUNT } from '@/constants/bi-sdk'
import { IFRAME_SEARCH_PARAM_KEYS, IframeSearchParamKeys } from '@/constants/iframe'
import { useAuthContext } from '@/context/AuthContext'
import { useBISdkContext } from '@/context/BISdkContext'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useVSCSdkContext } from '@/context/VSCSdkContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { useWorldIframeToken } from '@/context/WorldIframeTokenContext'
import { useDisableScrollLock } from '@/hooks/useDisableScrollLock'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useEnableViewMode } from '@/hooks/useEnableViewMode'
import { IframeHandlers } from '@/hooks/useIframeScrollLock'
import { useMessageDispatch } from '@/hooks/useMessageDispatch'
import { useMobileWorldRedirectUrlBuilder } from '@/hooks/useMobileWorldRedirectUrl'
import { useRouter } from '@/i18n/navigation'
import { getCreateDomain } from '@/lib/configs'
import { buildIframeUrl, getWorldIframeAllowPermissions } from '@/lib/iframe'
import { cn } from '@/lib/utils'
import { addWorldViewCount } from '@/services/cms'

type WorldIframeProps = {
  ref: RefObject<HTMLIFrameElement | null>
  handlers: IframeHandlers
  setIsShowSplash: Dispatch<SetStateAction<boolean>>
}

type WorldMessages = {
  'world-mounted': void
  'sign-in': void
  'url-change': {
    url: string
  }
  'world-exit': void
  'refresh-page': void
  'locale-changed': {
    locale: Locale
  }
  'debug-mode': {
    configs: unknown
  }
}

function WorldIframe({ setIsShowSplash, handlers, ref }: WorldIframeProps) {
  const { handleLogin } = useAuthContext()
  const { roomData, setRoomData } = useCheckEnterRoomContext()
  const { worldMeta } = useWorldDetailsContext()
  const { jwtToken } = useWorldIframeToken()
  const enableFull3d = useEnableFull3d()
  const enableViewMode = useEnableViewMode()
  const disableScrollLock = useDisableScrollLock()
  const getMobileRedirectUrl = useMobileWorldRedirectUrlBuilder()
  const searchParams = useSearchParams()
  const router = useRouter()

  const [isWebGLSupported, setIsWebGLSupported] = useState<boolean | null>(null)

  const isViverseSystemDisabled = worldMeta?.scene_settings?.disabledViverseSystem
  const [isLogoReadyToShow, setIsLogoReadyToShow] = useState(false)
  const isFull3D = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D)
  const shouldRenderViverseLogo = isViverseSystemDisabled && isLogoReadyToShow && isFull3D
  const { sendBIReport } = useBISdkContext()
  const { accountType } = useConfigsContext()

  const handleAddWorldViewCount = async () => {
    if (roomData?.hub_sid) {
      const data = await addWorldViewCount(roomData.hub_sid)
      if (data?.status === 200) {
        const prevViewCount = roomData?.view_count || 0
        setRoomData((prev) => ({
          ...prev,
          view_count: prev.view_count + 1,
        }))

        sendBIReport({
          ev: BI_EVENT_VIEW_COUNT,
          category: BI_CATEGORY_BEHAVIOR_IN_ROOM,
          a1: accountType,
          a2: location.href,
          a3: roomData.title,
          a4: roomData.hub_sid,
          a12: `${prevViewCount + 1}`,
        })
      }
    }
  }

  const checkWebGLSupport = (): boolean => {
    if (!window.WebGLRenderingContext) {
      return false
    }
    const canvas = document.createElement('canvas')
    let gl = null
    try {
      gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    } catch {
      return false
    }
    if (!gl) {
      return false
    }
    return true
  }

  const { changeLocale } = useVSCSdkContext()

  useEffect(() => {
    const isSupported = checkWebGLSupport()
    setIsWebGLSupported(isSupported)

    if (isSupported === false) {
      setIsShowSplash(false)
    } else {
      setIsShowSplash(true)
    }
  }, [setIsShowSplash])

  const otherParams = Object.fromEntries(
    Array.from(searchParams).filter(
      ([key]) => !Object.values(IFRAME_SEARCH_PARAM_KEYS).includes(key as IframeSearchParamKeys),
    ),
  )

  // 🥷🏼 Uncomment for local dev for in world (localhost:5173)
  // const createDomain = `https://localhost:5173`
  // const iframeUrl = `https://localhost:5173/${roomData.hub_sid}`
  const createDomain = `https://${getCreateDomain()}`
  const iframeUrl = roomData.hubs_url

  // Add JWT token to otherParams if available
  const iframeParams = {
    full3d: true,
    'no-cookie': true,
    view: enableViewMode,
    'no-scrolllock': disableScrollLock,
    otherParams: {
      ...otherParams,
      ...(jwtToken && { iframe_token: jwtToken }),
    },
  }

  const iframeSrc = buildIframeUrl(iframeUrl, iframeParams)

  useMessageDispatch<WorldMessages>(
    {
      'world-mounted': () => {
        setIsShowSplash(false)
        setIsLogoReadyToShow(true)
      },
      'sign-in': () =>
        handleLogin({
          // 🥷🏼 if has full3d, add full3d= to the login url
          ...(enableFull3d && {
            loginParams: {
              full3d: '',
            },
          }),
        }),
      'url-change': ({ url }) => {
        handleUrlChange(url)
      },
      'world-exit': () => {
        // TODO: go back to origin's URL if needed
        const exitWorldUrl = getMobileRedirectUrl({ isFull3d: false })
        handleUrlChange(exitWorldUrl)
      },
      'refresh-page': () => {
        window.location.reload()
      },
      'locale-changed': ({ locale }) => {
        changeLocale(locale)
      },
      'debug-mode': ({ configs }) => {
        window._CONFIGS = configs
      },
    },
    createDomain,
  )

  const handleUrlChange = (url: string) => {
    try {
      const targetUrl = new URL(url)
      const currentOrigin = window.location.origin

      if (targetUrl.origin === currentOrigin) {
        // Same origin - use Next.js router for client-side navigation with i18n support
        router.push(targetUrl.pathname + targetUrl.search + targetUrl.hash)
      } else {
        // Different origin - use window.location for full page navigation
        window.location.href = url
      }
    } catch (error) {
      console.error('url-change error', error)
      // If URL parsing fails, treat as relative path
      router.push(url)
    }
  }

  const handleIframeError = (e: SyntheticEvent<HTMLIFrameElement>) => {
    console.error('Iframe loading failed:', e)
    setIsShowSplash(false)
    setIsLogoReadyToShow(true)
  }

  const notifyChildWindowSize = useDebounceCallback(() => {
    const iframeWindow = ref.current?.contentWindow
    if (!iframeWindow) return

    iframeWindow.postMessage(
      {
        type: 'update-parent-window-size',
        payload: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
      createDomain,
    )
  }, 100)

  const notifyLogoVisibility = useDebounceCallback(() => {
    const iframeWindow = ref.current?.contentWindow
    if (!iframeWindow) return

    iframeWindow.postMessage(
      {
        type: 'viverse-logo-visibility',
        payload: {
          isVisible: isFull3D,
        },
      },
      createDomain,
    )
  }, 100)

  const handleIframeLoad = () => {
    console.log('🥷🏼 handleIframeLoad')
    notifyChildWindowSize()
    notifyLogoVisibility()
  }

  useEventListener('resize', notifyChildWindowSize)

  useTimeout(handleAddWorldViewCount, 10000)

  const allowAttr = getWorldIframeAllowPermissions().join('; ')

  // Show loading while checking WebGL support
  if (isWebGLSupported === null) {
    return null
  }

  // Show WebGL error message if not supported
  if (isWebGLSupported === false) {
    return (
      <div className="z-10 size-full">
        <table style={{ backgroundColor: '#8CE', width: '100%', height: '100%' }}>
          <tbody>
            <tr>
              <td align="center">
                <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
                  <div>
                    This page requires a browser that supports WebGL.
                    <br />
                    <a href="https://get.webgl.org">Click here to find out more.</a>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  // Don't render iframe if no JWT token
  if (jwtToken == null) {
    return null
  }

  return (
    <>
      {shouldRenderViverseLogo && <ViverseLogoOverlay isVisible />}
      <iframe
        id="world-iframe"
        src={iframeSrc}
        ref={ref}
        title="World Experience"
        className={cn('z-10 size-full border-none bg-transparent scheme-normal', {
          'hidden md:block': !enableFull3d,
        })}
        onError={handleIframeError}
        onLoad={handleIframeLoad}
        allow={allowAttr}
        allowFullScreen
        {...handlers}
      />
    </>
  )
}

export default WorldIframe
